import React, {useEffect, useState} from "react";
import {Col, Row} from "reactstrap";
import Divider from "@mui/material/Divider";
import Dialog from "../../base/dialog";
import Button from "../../base/button/button";
import Form from "../../base/form";
import ValidateMessages from "../../../../core/models/static/texts/vallidate-messages";
import {OutlinedInputForm} from "../../base/input/outlined_input";
import Api from "../../../../core/services/api/api";
import useIsMounted from "../../../hooks/use-is-mounted";
import {deepEqual} from "../../../../core/services/utils/utils";
import DateUtils from "../../../../core/services/date-utils";
import {MuiRFF, Yup} from '../../form';

const formNames = {
    title: 'title',
    link: 'link',
    startDate: 'startDate',
    endDate: 'endDate'
}


const schema = Yup.object().shape({
    [formNames.title]: Yup.string().trim().nullable().required(),
    [formNames.link]: Yup.string().trim().nullable().required(),
    [formNames.startDate]: Yup.mixed()
        .validDate(ValidateMessages.incorrect)
        .disablePastDate(ValidateMessages.minDate("today"), 'day')
        .maxDate(Yup.ref(formNames.endDate), ValidateMessages.lessThan("End Date"))
        .nullable().required(ValidateMessages.required),
    [formNames.endDate]: Yup.mixed()
        .validDate(ValidateMessages.incorrect)
        .disablePastDate(ValidateMessages.minDate("today"), 'day')
        .minDate(Yup.ref(formNames.startDate), ValidateMessages.greaterThan("Start Date"))
        .nullable(),
});

const validate = MuiRFF.makeValidate(schema);
const required = MuiRFF.makeRequired(schema);

const UpsertSurveyDialog = ({open, setOpen, setSurveys, selectedSurvey = null}) => {
    const [submitting, setSubmitting] = useState(false);
    const [initialData, setInitialData] = useState({});
    const isMounted = useIsMounted();

    /**
     * With each change in the state of dialog being open or selected survey:
     * Sets the initial values of the form in the state with survey values or just start date
     */
    useEffect(() => {
        if (selectedSurvey) {
            setInitialData({
                [formNames.title]: selectedSurvey.title,
                [formNames.link]: selectedSurvey.link,
                [formNames.startDate]: selectedSurvey.startDateTime ? DateUtils.serverToUTC(selectedSurvey.startDateTime) : undefined,
                [formNames.endDate]: selectedSurvey.endDateTime ? DateUtils.serverToUTC(selectedSurvey.endDateTime) : undefined,
            });
            return;
        }
        setInitialData({
            [formNames.startDate]: DateUtils.UTCNow(),
        });
    }, [selectedSurvey, open])

    /**
     * Adds the new survey to the system:
     * - Calls the required api for this action
     * - if the api call was successful, adds the survey to the top of the list
     * @param values
     */
    const addNewSurvey = async (values) => {
        const forApi = {
            title: values[formNames.title],
            link: values[formNames.link],
            startDateTime: values[formNames.startDate] ? DateUtils.UTCToServer(values[formNames.startDate]).format(DateUtils.withoutOffsetFormat) : null,
            endDateTime: values[formNames.endDate] ? DateUtils.UTCToServer(values[formNames.endDate]).format(DateUtils.withoutOffsetFormat) : null,
        }
        setSubmitting(true);
        const response = await Api.addNewSurvey(forApi);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setSurveys(prevState => [
                ...prevState,
                response?.data
            ]);
            setOpen(false);
        }
        setSubmitting(false);
    }

    /**
     * Edits the survey in the system:
     * - Calls the api regarding this action
     * - if the api call was successful,edits the survey that is in the list
     * @param values
     */
    const editSurvey = async (values) => {
        setSubmitting(true);
        const forApi = {
            id: selectedSurvey?.id,
            title: null,
            link: null,
            startDateTime: values[formNames.startDate] ? DateUtils.UTCToServer(values[formNames.startDate]).format(DateUtils.withoutOffsetFormat) : null,
            endDateTime: values[formNames.endDate] ? DateUtils.UTCToServer(values[formNames.endDate]).format(DateUtils.withoutOffsetFormat) : null,
        };

        if (values[formNames.title].trim() !== initialData?.title?.trim())
            forApi.title = values[formNames.title].trim();

        if (values[formNames.link].trim() !== initialData.link?.trim())
            forApi.link = values[formNames.link].trim()

        const response = await Api.updateSurvey(forApi);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setSurveys(prevState => prevState.map(e => e.id !== response?.data?.id ? e : response.data))
            setOpen(false);
        }
        setSubmitting(false);
    }

    return (
        <Dialog
            open={open}
            setOpen={setOpen}
            className={'upsert-survey-dialog'}
        >
            <Row className='no-gutters justify-content-center align-items-start w-100'>
                <Row xs={12} className='no-gutters justify-content-center m-3 w-100'>
                    <p className={`text-lg text-color-1 my-2 font-weight-semi-bold text-center`}>
                        {selectedSurvey !== null ? "Edit the Survey" : "Add a New Survey"}
                    </p>
                </Row>
                <Form
                    className='w-100'
                    onSubmit={selectedSurvey === null ? addNewSurvey : editSurvey}
                    validate={validate}
                    initialValues={initialData}
                    render={({values, initialValues}) => {
                        const notChange = deepEqual(values, initialValues);
                        return (
                            <Row className='no-gutters justify-content-around w-100'>
                                <Row className='no-gutters justify-content-around w-100 mx-4'>
                                    <Col xs={12} className={'mb-3 px-1'}>
                                        <OutlinedInputForm

                                            label={"Title"}
                                            required={required[formNames.title]}
                                            name={formNames.title}
                                        />
                                    </Col>
                                    <Col xs={12} className={'mb-4 px-1  link-input-container'}>
                                        <OutlinedInputForm
                                            label={"Link"}
                                            minRows={6}
                                            helperText={"The link should be an HTML tag that starts with iframe"}
                                            multiline
                                            required={required[formNames.link]}
                                            name={formNames.link}
                                        />
                                    </Col>
                                    <Col xs={12} lg={6} className={'mb-3 px-1'}>
                                        <MuiRFF.DatePicker
                                            name={formNames.startDate}
                                            label={"Start Date"}
                                            required={required[formNames.startDate]}
                                            maxDate={values[formNames.endDate]}
                                            disablePast
                                        />
                                    </Col>
                                    <Col xs={12} lg={6} className={'mb-3 px-1'}>
                                        <MuiRFF.DatePicker
                                            name={formNames.endDate}
                                            label={"End Date (optional)"}
                                            required={required[formNames.endDate]}
                                            minDate={values[formNames.endDate]}
                                            disablePast
                                        />
                                    </Col>
                                </Row>
                                <div className='my-2 w-100'/>
                                <Divider className='divider'/>
                                <Row className='no-gutters justify-content-between align-items-start w-100'>
                                    <Row className='no-gutters justify-content-between align-items-start w-100'>
                                        <Button
                                            disabled={!open || submitting}
                                            variant={'dialog'}
                                            color={'primary'}
                                            onClick={() => setOpen(false)}
                                            borderRadius={'0 8px 0 8px'}
                                        >
                                            {submitting ? 'loading...' : 'Close'}
                                        </Button>
                                        <Button
                                            disabled={!open || submitting || notChange}
                                            variant={'dialog'}
                                            color={'primary'}
                                            type={'submit'}
                                            borderRadius={'8px 0 8px 0'}
                                        >
                                            {
                                                selectedSurvey
                                                    ? submitting
                                                        ? 'Submitting...'
                                                        : 'Edit'
                                                    : submitting
                                                        ? 'Adding...'
                                                        : 'Add'
                                            }
                                        </Button>
                                    </Row>
                                </Row>
                            </Row>
                        );
                    }}
                />
                <Divider className='divider'/>
            </Row>
        </Dialog>
    );
}


export default UpsertSurveyDialog;
